// F5: ANY scope change that makes a person LOSE companies asks first — not only
// the tenant's first scoped assignment (that 409 gate, lockoutConfirm, is
// unchanged and still runs after this). The server computes the person's reach
// before vs after with the enforcement algorithm (POST
// rbac/assignments/:id/scope-impact); we show the companies and the plain-words
// surfaces they lose in the branded confirm (never window.confirm). Widening
// never asks. A failed check is NOT "loses nothing": we say we couldn't check
// and ask.
import React, { useCallback } from 'react';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getScopeImpact,
  type AssignmentScopeInput,
  type ScopeImpact,
} from '@/services/rbacService';

const SHOWN = 8;

/** "a, b and c". */
export function joinWords(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join('; ')} and ${items[items.length - 1]}`;
}

export const ScopeImpactMessage: React.FC<{
  impact: ScopeImpact;
  personName: string;
  roleLabel: string;
  entityName: (id: string) => string;
}> = ({ impact, personName, roleLabel, entityName }) => {
  const names = impact.companiesLost.map(entityName);
  const shown = names.slice(0, SHOWN);
  const more = names.length - shown.length;
  const partlyLost = impact.surfacesLost.filter((s) => !impact.surfacesLostEntirely.includes(s));
  return (
    <div className="space-y-3 text-left" data-testid="scope-impact-message">
      <p>
        Saving this changes where “{roleLabel}” applies, and {personName} will lose access they have today.
      </p>
      {shown.length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground">Companies they lose</p>
          <ul className="mt-1 max-h-40 overflow-y-auto rounded-[10px] border border-border divide-y divide-border">
            {shown.map((n, i) => (
              <li key={`${impact.companiesLost[i]}`} className="px-3 py-1.5 text-sm text-foreground">
                {n}
              </li>
            ))}
            {more > 0 && <li className="px-3 py-1.5 text-xs text-muted-foreground">+{more} more</li>}
          </ul>
        </div>
      )}
      {impact.companiesUnknown && (
        <p className="text-sm">
          They lose every company except the ones you selected — we couldn’t load the company list to name them.
        </p>
      )}
      {partlyLost.length > 0 && (
        <p className="text-sm">
          <span className="font-medium text-foreground">There they will stop seeing: </span>
          {joinWords(partlyLost)}.
        </p>
      )}
      {impact.surfacesLostEntirely.length > 0 && (
        <p className="text-sm font-medium text-foreground">
          They will no longer see {joinWords(impact.surfacesLostEntirely)} at all.
        </p>
      )}
    </div>
  );
};

export interface ScopeImpactRequest {
  /** `<userId>_<roleId>` — also valid for a role not assigned yet. */
  assignmentId: string;
  scope: Pick<AssignmentScopeInput, 'entityScope' | 'legalEntityIds'>;
  personName: string;
  roleLabel: string;
  entityName: (id: string) => string;
}

/**
 * Ask before a scope change that removes access. Resolves true to go ahead
 * (nothing lost, or the admin confirmed), false to save nothing.
 */
export function useConfirmScopeImpact() {
  const confirm = useConfirm();
  return useCallback(
    async (req: ScopeImpactRequest): Promise<boolean> => {
      let impact: ScopeImpact;
      try {
        impact = await getScopeImpact(req.assignmentId, req.scope);
      } catch (e) {
        return confirm({
          title: 'Couldn’t check what changes',
          message: (
            <div className="space-y-2 text-left">
              <p>
                We couldn’t check whether this removes any of {req.personName}’s payroll, salary or accounting
                access.
              </p>
              <p className="text-xs text-muted-foreground">
                {getApiErrorMessage(e, 'The access check did not answer.')}
              </p>
            </div>
          ),
          confirmLabel: 'Save anyway',
          cancelLabel: 'Cancel',
          danger: true,
        });
      }
      if (!impact.narrows) return true;
      return confirm({
        title: `${req.personName} will lose access`,
        message: (
          <ScopeImpactMessage
            impact={impact}
            personName={req.personName}
            roleLabel={req.roleLabel}
            entityName={req.entityName}
          />
        ),
        confirmLabel: 'Save and remove access',
        cancelLabel: 'Cancel',
        danger: true,
      });
    },
    [confirm],
  );
}
